<!-- ai-agent.component.html — Phase 7 (EN-3700) in-portal Agent full-page surface, branded Claire (EN-4447) -->
<!-- EN-5079: masked in Datadog session replay; questions and answers are client financial data. -->
<div class="agent-page" data-dd-privacy="mask">
	<div class="agent-container">
		<div class="agent-header">
			<div class="agent-title">
				<img class="claire-avatar" src="assets/img/claire/claire-avatar.png" alt="Claire" />
				<span class="claire-wordmark">Cl<span class="claire-wordmark-ai">ai</span>re</span>
			</div>
			<div class="agent-header-actions">
				<!-- EN-4449: per-module sample questions PDF, generated from Andrew's question list. -->
				<a
					class="sample-questions-link"
					href="assets/docs/claire-sample-questions.pdf"
					target="_blank"
					rel="noopener noreferrer"
					title="Open a PDF of sample questions you can ask Claire"
				>
					<span class="material-icons">menu_book</span> Sample questions
				</a>
				<label class="model-select-wrap" *ngIf="models.length > 0" title="Choose the model">
					<select
						class="model-select"
						[ngModel]="selectedModel"
						(ngModelChange)="onModelChange($event)"
						[disabled]="isStreaming"
					>
						<option *ngFor="let m of models" [value]="m.id">{{ m.displayName }}</option>
					</select>
					<span class="material-icons model-select-caret">expand_more</span>
				</label>
				<!-- EN-5079: saving keeps this conversation in Saved chats; later turns are added automatically. -->
				<button
					class="new-chat-btn save-chat-btn"
					*ngIf="isSaved || canSave || isSaving"
					(click)="saveChat()"
					[disabled]="!canSave"
					[title]="isSaved ? 'This chat is in Saved chats' : 'Keep this chat in Saved chats'"
				>
					<span class="material-icons">{{ isSaved ? 'bookmark' : 'bookmark_border' }}</span>
					{{ isSaved ? 'Saved' : isSaving ? 'Saving…' : 'Save chat' }}
				</button>
				<button class="new-chat-btn" (click)="newChat()" [disabled]="isStreaming" title="Start a new task">
					<span class="material-icons">add</span> New chat
				</button>
			</div>
		</div>

		<div class="agent-wrapper scrollable" #chatScroll>
			<!-- Empty-state: centered greeting + suggestion chips, shown until the first turn. -->
			<div class="agent-empty" *ngIf="messages.length === 0">
				<div class="agent-empty-mark">
					<img class="claire-avatar-large" src="assets/img/claire/claire-avatar.png" alt="Claire" />
				</div>
				<div class="agent-empty-title">How can I help, {{ firstName }}?</div>
				<div class="agent-empty-sub">
					I can look up and act on your ERP data — find a vendor, draft a check request, check a budget, and more.
				</div>
				<div class="agent-suggestions">
					<button class="suggestion" (click)="useSuggestion('Find a vendor named ')">Find a vendor</button>
					<button class="suggestion" (click)="useSuggestion('Draft a check request for ')">
						Draft a check request
					</button>
					<button class="suggestion" (click)="useSuggestion('Show me the budget for ')">Check a budget</button>
					<button class="suggestion" (click)="useSuggestion('What forms are awaiting my approval?')">
						What's awaiting my approval?
					</button>
				</div>
				<!-- EN-4448: route how-to/documentation questions to the help assistant, whose launcher
				     sits bottom-right on every portal page (including this one). -->
				<div class="agent-empty-hint">
					Looking for how-to or documentation help? Ask <strong>Andi</strong> instead: the Andi button in the
					bottom-right corner of the screen.
				</div>
			</div>

			<div
				*ngFor="let msg of messages"
				[ngClass]="{ 'message-row': true, left: msg.role === 'assistant', right: msg.role === 'user' }"
			>
				<div *ngIf="msg.role === 'user'" class="user-question">
					<div class="q-attachments" *ngIf="msg.attachments?.length">
						<span class="attachment-chip readonly" *ngFor="let a of msg.attachments">
							<span class="material-icons">description</span>
							<span class="attachment-name">{{ a.fileName }}</span>
						</span>
					</div>
					<div class="q-text" data-dd-action-name="Claire question">{{ msg.content }}</div>
				</div>

				<div *ngIf="msg.role === 'assistant'" class="agent-answer-block">
					<div class="tool-activity" *ngIf="msg.tools.length > 0 || msg.codeExec">
						<span class="tool-chip" *ngFor="let tool of msg.tools">
							<span class="material-icons">build</span> {{ tool }}
						</span>
						<!-- Code-execution status chip: spins while the server-side run is in flight, then
						     settles to a done/failed state. Keeps the user informed through the long, silent
						     execution window (the gap the SSE heartbeat also protects). -->
						<span
							class="tool-chip code-exec-chip"
							*ngIf="msg.codeExec"
							[class.running]="msg.codeExec === 'running'"
							[class.failed]="msg.codeExec === 'failed'"
						>
							<span class="material-icons">{{
								msg.codeExec === 'running' ? 'sync' : msg.codeExec === 'failed' ? 'error_outline' : 'done'
							}}</span>
							{{
								msg.codeExec === 'running'
									? 'Running analysis…'
									: msg.codeExec === 'failed'
									? 'Analysis failed'
									: 'Analysis complete'
							}}
						</span>
					</div>

					<div class="answer-content" data-dd-action-name="Claire answer">
						<span [innerHTML]="renderContent(msg)"></span><span class="stream-cursor" *ngIf="msg.pending">▍</span>
					</div>

					<!-- Calm, non-error notice when the turn ended on a protective stop reason (e.g. the
					     model's max single-reply output truncated the response). EN-4014. -->
					<div class="answer-notice" *ngIf="msg.notice">
						<span class="material-icons">info_outline</span>
						<span>{{ msg.notice }}</span>
					</div>

					<!-- Files the Agent authored this turn (code-execution outputs). Links are short-lived. -->
					<div class="agent-files" *ngIf="msg.files?.length">
						<a
							class="agent-file-chip"
							*ngFor="let f of msg.files"
							[href]="f.downloadUrl"
							target="_blank"
							rel="noopener noreferrer"
							[attr.download]="f.fileName"
						>
							<span class="material-icons">download</span> {{ f.fileName }}
						</a>
					</div>

					<!-- EN-5079: files from a reopened saved chat. Their links expired an hour after the turn. -->
					<div class="agent-files" *ngIf="msg.savedFiles?.length">
						<span
							class="agent-file-chip expired"
							*ngFor="let name of msg.savedFiles"
							title="This download link has expired"
						>
							<span class="material-icons">insert_drive_file</span> {{ name }}
							<span class="agent-file-expired">link expired</span>
						</span>
					</div>

					<div class="thinking" *ngIf="msg.pending && !msg.content && msg.tools.length === 0 && !msg.codeExec">
						<div class="typing-indicator"><span></span><span></span><span></span></div>
					</div>

					<!-- Per-round elapsed-time indicator. Token/cost figures are intentionally NOT shown:
					     Andrew (2026-06-10) wants no dollar amounts in front of clients during demos; usage
					     is still metered and persisted server-side (AgentTokenUsages) for behind-the-scenes review. -->
					<div class="turn-meta" *ngIf="!msg.local && (msg.pending || (msg.elapsedMs || 0) > 0)">
						<span class="turn-meta-spinner" *ngIf="msg.pending">✳</span>
						<span>{{ formatElapsed(msg.elapsedMs) }}</span>
					</div>
				</div>
			</div>

			<!-- Cap-hit: a calm contact-admin state, not an error toast. -->
			<div class="cap-hit-card" *ngIf="capHit">
				<span class="material-icons">account_balance_wallet</span>
				<div>
					<div class="cap-hit-title">AI token limit reached</div>
					<div class="cap-hit-body">{{ capHit.error }}</div>
				</div>
			</div>

			<!--
				EN-4740 credit chip. Drawn only when there is something to say: a limit to count
				against, an exhausted balance, or staff status. A client with credits and no limit
				sees no chrome. Tokens only, never dollars (decision 27).
			-->
			<div class="credit-chip" [ngClass]="creditWarning" *ngIf="showCreditChip">
				<span class="material-icons">account_balance_wallet</span>

				<span *ngIf="credits?.isAclarianStaff && creditWarning !== 'blocked'" class="credit-chip-text">
					Aclarian staff: this turn does not use the client's credits.
				</span>

				<span *ngIf="!credits?.isAclarianStaff && credits?.limitCap" class="credit-chip-text">
					{{ credits?.limitUsed | number }} of {{ credits?.limitCap | number }} tokens used
					<span class="credit-chip-reset">({{ creditResetLabel }})</span>
				</span>

				<span
					*ngIf="!credits?.isAclarianStaff && credits?.limitFiguresHidden && credits?.percentUsed"
					class="credit-chip-text"
				>
					{{
						credits?.percentUsed === 100
							? 'Your organization has reached its AI token limit'
							: 'Your organization is close to its AI token limit'
					}}
					<span class="credit-chip-reset">({{ creditResetLabel }})</span>
				</span>

				<!--
					No staff exclusion here, deliberately. A staff caller CAN be refused (the ladder
					denies before it reaches the staff skip), and when that happens the reason is the
					only useful thing the chip can say. The staff reassurance above steps aside for
					exactly the same reason: it would otherwise claim the turn ran. Once a refused turn
					has put the limit card on screen, the card says it and the chip does not repeat it.
				-->
				<span *ngIf="creditWarning === 'blocked' && !capHit" class="credit-chip-text">
					{{ credits?.message }}
				</span>
			</div>

			<div class="agent-error" *ngIf="errorMessage">
				<span class="material-icons">error_outline</span> {{ errorMessage }}
			</div>
		</div>

		<div class="agent-input-container">
			<!-- Staged attachments for the next turn -->
			<div class="attachment-bar" *ngIf="pendingAttachments.length > 0">
				<span class="attachment-chip" *ngFor="let a of pendingAttachments; let i = index">
					<span class="material-icons">description</span>
					<span class="attachment-name">{{ a.fileName }}</span>
					<button
						type="button"
						class="attachment-remove"
						(click)="removeAttachment(i)"
						[disabled]="isStreaming"
						title="Remove"
					>
						<span class="material-icons">close</span>
					</button>
				</span>
			</div>
			<div class="attachment-error" *ngIf="attachmentError">{{ attachmentError }}</div>

			<div class="composer">
				<textarea
					class="agent-input"
					[(ngModel)]="userInput"
					[maxlength]="charLimit"
					placeholder="Ask Claire to find, create, or check something in your ERP, or attach an invoice..."
					rows="1"
					(keydown.enter)="$event.preventDefault(); sendMessage()"
				></textarea>
				<div class="agent-input-footer">
					<div class="agent-input-footer-left">
						<input
							#fileInput
							type="file"
							[accept]="acceptedFileTypes"
							multiple
							hidden
							(change)="onFilesSelected($event)"
						/>
						<button
							type="button"
							class="attach-btn"
							(click)="fileInput.click()"
							[disabled]="isStreaming"
							title="Attach a file (PDF or image)"
						>
							<span class="material-icons">attach_file</span>
						</button>
						<span class="char-count">{{ charCount }}</span>
					</div>
					<button
						class="send-btn"
						(click)="sendMessage()"
						[disabled]="isStreaming || isLoadingChat || (!userInput.trim() && pendingAttachments.length === 0)"
						title="Send"
					>
						<span class="material-icons">arrow_upward</span>
					</button>
				</div>
			</div>
		</div>
	</div>
</div>
